import React, { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff, RefreshCw, CloudOff } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { getOfflineQueueCount, getPendingRecordingsCount } from '../utils/offlineDB';
import { syncPendingRecordings } from '../utils/syncService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConnectionStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pingLatency, setPingLatency] = useState(null);
  const [isPinging, setIsPinging] = useState(false);
  const [offlineCount, setOfflineCount] = useState(0);
  const [pendingRecordings, setPendingRecordings] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Update offline counts from IndexedDB
  const updateOfflineCounts = useCallback(async () => {
    try {
      const queueCount = await getOfflineQueueCount();
      const recordingsCount = await getPendingRecordingsCount();
      setOfflineCount(queueCount);
      setPendingRecordings(recordingsCount);
    } catch (error) {
      console.error('Error getting offline counts:', error);
    }
  }, []);

  // Sync pending recordings to Azure when online
  const syncOfflineData = useCallback(async () => {
    if (!navigator.onLine) return;
    try {
      console.log('[Sync] Starting sync of pending recordings...');
      const result = await syncPendingRecordings();
      console.log('[Sync] Result:', result);
      if (result.synced > 0) {
        toast.success(`Synced ${result.synced} recording${result.synced > 1 ? 's' : ''} to cloud`);
      }
    } catch (err) {
      console.error('[Sync] Auto-sync failed:', err);
    }
    await updateOfflineCounts();
  }, [updateOfflineCounts]);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online! Syncing data...');
      syncOfflineData();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setPingLatency(null);
      toast.error('You are offline. Data will be saved locally.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncOfflineData]);

  // Ping the server to check connection quality
  const pingServer = useCallback(async () => {
    if (!navigator.onLine) {
      setPingLatency(null);
      return;
    }

    setIsPinging(true);
    const startTime = performance.now();

    try {
      const response = await fetch(`${API_URL}/health`, { method: 'GET', cache: 'no-store' });
      const latency = Math.round(performance.now() - startTime);

      if (response.ok) {
        setPingLatency(latency);
        setIsOnline(true);
      } else {
        setPingLatency(null);
      }
    } catch (error) {
      setPingLatency(null);
      if (navigator.onLine) setIsOnline(false);
    } finally {
      setIsPinging(false);
    }
  }, []);

  // Periodic ping every 30 seconds
  useEffect(() => {
    pingServer();
    const interval = setInterval(pingServer, 30000);
    return () => clearInterval(interval);
  }, [pingServer]);

  // Update offline counts every 10 seconds
  useEffect(() => {
    updateOfflineCounts();
    const interval = setInterval(updateOfflineCounts, 10000);
    return () => clearInterval(interval);
  }, [updateOfflineCounts]);

  // Handle manual refresh button
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      window.dispatchEvent(new CustomEvent('app-refresh'));
      await pingServer();
      await updateOfflineCounts();
      if (navigator.onLine) await syncOfflineData();
      toast.success('Refreshed successfully');
    } catch (error) {
      toast.error('Refresh failed');
    } finally {
      setIsRefreshing(false);
    }
  };

  const getPingColor = () => {
    if (!isOnline || pingLatency === null) return 'text-red-500';
    if (pingLatency < 100) return 'text-green-500';
    if (pingLatency < 300) return 'text-yellow-500';
    return 'text-orange-500';
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (pingLatency === null) return 'Checking...';
    return `${pingLatency}ms`;
  };

  const totalPending = offlineCount + pendingRecordings;

  return (
    <div
      className="fixed top-4 right-4 z-50 flex items-center gap-2"
      data-testid="connection-status"
    >
      {totalPending > 0 && (
        <div
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full"
          title={`${totalPending} items pending sync`}
          data-testid="pending-indicator"
        >
          <CloudOff className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-xs font-medium text-amber-400">{totalPending}</span>
        </div>
      )}

      <div
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-all ${isOnline
          ? 'bg-zinc-900/90 border-zinc-700/50'
          : 'bg-red-500/20 border-red-500/30'
          }`}
        title={isOnline ? `Connected - ${getStatusText()}` : 'Offline - Data saved locally'}
        data-testid="ping-indicator"
      >
        {isOnline ? (
          <>
            <Wifi className={`w-3.5 h-3.5 ${getPingColor()}`} />
            <span className={`text-xs font-medium ${getPingColor()}`}>{getStatusText()}</span>
            {isPinging && <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />}
          </>
        ) : (
          <>
            <WifiOff className="w-3.5 h-3.5 text-red-400" />
            <span className="text-xs font-medium text-red-400">Offline</span>
          </>
        )}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={handleRefresh}
        disabled={isRefreshing}
        className={`h-8 w-8 p-0 rounded-full bg-zinc-900/90 border border-zinc-700/50 hover:bg-zinc-800 ${isRefreshing ? 'animate-spin' : ''
          }`}
        title="Refresh data"
        data-testid="refresh-btn"
      >
        <RefreshCw className={`w-4 h-4 text-zinc-400 ${isRefreshing ? 'animate-spin' : ''}`} />
      </Button>
    </div>
  );
};

export default ConnectionStatus;
